import { useEffect, useRef, useState } from 'react';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import LogLevelBadge from './ds/LogLevelBadge';
import { ExpandIcon } from './ds/icons';
import { ContainerLog } from '@back4app2/sdk';
import LogOverlay from "./LogOverlay";

export type LogBoxProps = {
  errorMessage?: string;
  logs?: ContainerLog[];
  isLoading: boolean;
  canMaximize?: boolean;
  fullHeight?: boolean;
}

const ContainerLogBox = ({ errorMessage, logs, isLoading, canMaximize, fullHeight }: LogBoxProps) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      if (!errorMessage && logs && autoScroll && boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
    },
    [
      errorMessage,
      logs,
      autoScroll
    ]
  );

  const onScroll = () => {
    if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) >= 0.99 * boxRef.current.scrollHeight && !autoScroll) {
      setAutoScroll(true);
    } else if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) < 0.99 * boxRef.current.scrollHeight && autoScroll) {
      setAutoScroll(false);
    }
  };

  /**
   * Expanded, not full-screen.
   *
   * `requestFullscreen` handed the whole viewport to a bare <div>, which cost
   * us the browser's own "Press Esc to exit full screen" overlay — a message
   * no script of ours can dismiss — and left the exit control stranded inside
   * a scrolling box. Our own overlay costs nothing, puts the control in the
   * corner where it belongs, and closes on Escape like every other layer.
   */
  const handleFullScreen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isLoading || errorMessage) {
      return;
    }
    e.preventDefault();
    setIsMaximized(true);
  }

  const exitFullScreen = () => setIsMaximized(false);

  useEffect(() => {
    if (!isMaximized) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMaximized(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMaximized]);

  /**
   * Runtime lines carry no level of their own — only the stream they came
   * from. stderr reads as an error, stdout as system output.
   */
  const renderLines = () => (
    <div className="flex flex-col">
      {logs!.map((log, i) => (
        <div
          key={log.id}
          className={[
            'flex flex-col gap-1 px-3.5 py-[5px] sm:flex-row sm:items-start sm:gap-3',
            i % 2 ? 'bg-white/[0.015]' : '',
          ].join(' ')}
        >
          <span className="font-courrier text-ds-115 text-navy-300 flex-none sm:w-[132px]">
            {log.time.toISOString().slice(11, 23)}
          </span>
          <LogLevelBadge level={log.source === 'stderr' ? 'error' : 'system'} fixedWidth />
          <span className="font-courrier text-ds-115 text-navy-300 flex-none hidden lg:inline">
            {log.container.id.split('-')[0]}
          </span>
          <span className="font-courrier text-ds-115 leading-[1.5] text-navy-100 flex-1 min-w-0 break-words">
            {log.message}
          </span>
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className="flex h-full justify-center items-center py-10">
      <span className="text-ds-135 text-navy-300">No log lines yet.</span>
    </div>
  );

  const body = isLoading ? (
    <div className="flex h-full justify-center items-center py-10">
      <div className="flex flex-col justify-center items-center">
        <LoadingSpinner />
        <div className="mt-4 text-ds-135 text-navy-200">Loading logs</div>
      </div>
    </div>
  ) : errorMessage ? (
    <ErrorMessage message={errorMessage} />
  ) : logs && logs.length > 0 ? renderLines() : emptyState;

  if (isMaximized) {
    return <LogOverlay label="application log" onClose={exitFullScreen}>{body}</LogOverlay>;
  }

  return (<div className={`relative ${fullHeight ? 'h-full' : ''}`}>
    {canMaximize && !isMaximized ? (
      <button
        type="button"
        aria-label="Expand log"
        onClick={isMaximized ? exitFullScreen : handleFullScreen}
        className="b4-focus z-10 absolute top-2.5 right-2.5 w-[30px] h-[30px] rounded-control border border-navy-400 bg-navy-700 text-navy-100 flex items-center justify-center hover:bg-navy-600 transition-colors duration-[120ms]"
      >
        <ExpandIcon size={14} />
      </button>
    ) : null}
    <div
      className="relative py-1.5 bg-navy-950 overflow-y-auto b4-thin"
      ref={boxRef}
      onScroll={onScroll}
      style={{ height: fullHeight ? '100%' : '26rem' }}
    >
      {body}
    </div>
  </div>);
}

export default ContainerLogBox;


